import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    ChevronDown16Regular,
    ChevronUp16Regular,
    Dismiss16Regular,
    Document16Regular,
} from '@fluentui/react-icons';
import {
    getCitationDisplayTitle,
    isIrlCitation,
    isObservationCitation,
    linkCitationChunk,
} from '../../utils/citationDisplay';
import { formatEvidenceCoverageLabel } from '../../utils/lensIntelligenceResponseMeta';
import styles from './askAi.module.css';

function findCitationForItem(item, citations, chunks) {
    const list = Array.isArray(citations) ? citations : [];
    if (item?.ref != null) {
        const byRef = list.find(c => String(c?.ref) === String(item.ref));
        if (byRef) return byRef;
    }
    const title = item?.title || item?.filename;
    if (!title) return null;
    return (
        list.find(c => {
            const linked = linkCitationChunk(c, chunks);
            return getCitationDisplayTitle(c, linked) === title;
        }) || null
    );
}

function resolveSourceMeta(item, citations, chunks) {
    const citation = findCitationForItem(item, citations, chunks);
    const linked = citation ? linkCitationChunk(citation, chunks) : null;
    const st = String(
        citation?.source_type || linked?.source_type || item?.bucket || '',
    )
        .toLowerCase()
        .trim();

    const page =
        citation?.page_number ??
        citation?.page ??
        linked?.page_number ??
        linked?.page ??
        null;
    const filename =
        item?.filename ||
        citation?.document_name ||
        citation?.metadata_storage_name ||
        linked?.document_name ||
        linked?.metadata_storage_name ||
        null;

    if (isObservationCitation(citation, linked) || st === 'observation') {
        const ws = citation?.workstream_repr || linked?.workstream_repr || 'Observation';
        const tag = citation?.tag_repr || linked?.tag_repr;
        return {
            typeLabel: 'Observation',
            path: tag ? `${ws} / ${tag}` : ws,
            preview: citation?.text_preview || linked?.chunk || item?.detail || null,
            citation,
            linked,
        };
    }

    if (isIrlCitation(citation, linked) || st === 'irl' || st === 'irl_row') {
        const ref = citation?.irl_ref || linked?.irl_ref || item?.title;
        const ws =
            citation?.workstream_name ||
            linked?.workstream_name ||
            citation?.workstream_repr ||
            'IRL';
        return {
            typeLabel: 'IRL Response',
            path: ref ? `${ws} / ${ref}` : ws,
            preview:
                citation?.answer_excerpt ||
                citation?.request_excerpt ||
                linked?.answer_excerpt ||
                item?.detail ||
                null,
            citation,
            linked,
        };
    }

    if (st.includes('journal') || st.includes('note')) {
        const title = item?.title || filename || 'Journal entry';
        return {
            typeLabel: st.includes('meeting') ? 'Meeting Note' : 'Journal Entry',
            path: title.includes('—')
                ? title.split('—').map(s => s.trim()).join(' / ')
                : title,
            preview: citation?.text_preview || linked?.chunk || item?.detail || null,
            citation,
            linked,
        };
    }

    if (st.includes('meeting')) {
        return {
            typeLabel: 'Meeting Note',
            path: item?.title || filename || 'Meeting note',
            preview: citation?.text_preview || linked?.chunk || item?.detail || null,
            citation,
            linked,
        };
    }

    const docName = filename || item?.title || 'Document';
    const pageSuffix =
        page != null && Number.isFinite(Number(page)) ? ` - p. ${Number(page)}` : '';
    return {
        typeLabel: 'VDR Document',
        path: `VDR / ${docName}${pageSuffix}`,
        preview: citation?.text_preview || linked?.chunk || item?.detail || null,
        citation,
        linked,
    };
}

function EvidenceSourceCard({ item, citations, chunks, onOpen }) {
    const [expanded, setExpanded] = useState(false);
    const meta = useMemo(
        () => resolveSourceMeta(item, citations, chunks),
        [item, citations, chunks],
    );
    const detail =
        item?.reasonRetained ||
        item?.authorityClass ||
        meta.preview ||
        item?.detail ||
        null;

    return (
        <div className={styles.evidenceCard}>
            <div className={styles.evidenceCardRow}>
                <div className={styles.evidenceCardIcon} aria-hidden>
                    <Document16Regular />
                </div>
                <button
                    type="button"
                    className={styles.evidenceCardMain}
                    onClick={() => onOpen?.(item)}>
                    <div className={styles.evidenceCardTitleRow}>
                        <span className={styles.evidenceCardTitle}>
                            {item.title || item.filename || 'Source'}
                        </span>
                        <span className={styles.evidenceTypeTag}>{meta.typeLabel}</span>
                    </div>
                    {meta.path ? (
                        <span className={styles.evidenceCardPath}>{meta.path}</span>
                    ) : null}
                </button>
                <button
                    type="button"
                    className={styles.evidenceCardChevron}
                    aria-expanded={expanded}
                    aria-label={expanded ? 'Collapse source' : 'Expand source'}
                    onClick={() => setExpanded(v => !v)}>
                    {expanded ? <ChevronUp16Regular /> : <ChevronDown16Regular />}
                </button>
            </div>
            {expanded && detail ? (
                <div className={styles.evidenceCardDetail}>
                    {item?.reasonRetained ? (
                        <p className={styles.evidenceCardDetailLine}>{item.reasonRetained}</p>
                    ) : null}
                    {item?.authorityClass ? (
                        <p className={styles.evidenceCardDetailLine}>
                            Authority: {item.authorityClass}
                        </p>
                    ) : null}
                    {meta.preview ? (
                        <p className={styles.evidenceCardPreview}>{meta.preview}</p>
                    ) : !item?.reasonRetained && !item?.authorityClass ? (
                        <p className={styles.evidenceCardDetailLine}>No additional detail.</p>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}

const AskAiEvidenceSidebar = ({
    open,
    onClose,
    model,
    citations = [],
    chunks = [],
    confidence,
    coveragePct,
    sourcesUsedCount,
    sourcesTotalCount,
    processingCount = 0,
    onItemClick,
}) => {
    const evidenceItems = useMemo(() => {
        if (!model?.sections) return [];
        const items = [];
        for (const section of model.sections) {
            if (section.key === 'excluded') continue;
            if (section.items?.length) items.push(...section.items);
        }
        return items;
    }, [model]);

    useEffect(() => {
        if (!open) return undefined;
        const onKey = e => {
            if (e.key === 'Escape') onClose?.();
        };
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', onKey);
        return () => {
            document.body.style.overflow = prevOverflow;
            document.removeEventListener('keydown', onKey);
        };
    }, [open, onClose]);

    if (!open || !model) return null;

    const cardCount = evidenceItems.length;
    const used = sourcesUsedCount ?? cardCount;
    const total = sourcesTotalCount ?? used;
    const confidenceLabel = confidence
        ? `${String(confidence).charAt(0).toUpperCase()}${String(confidence).slice(1)} confidence`
        : null;
    const coverageLabel =
        coveragePct != null && Number.isFinite(Number(coveragePct))
            ? formatEvidenceCoverageLabel(coveragePct)
            : null;
    const sourcesLabel =
        total > used ? `${used} of ${total} sources` : `${cardCount} source${cardCount === 1 ? '' : 's'}`;
    const processing =
        processingCount != null && Number(processingCount) > 0 ? Number(processingCount) : 0;

    return createPortal(
        <div className={styles.evidenceOverlay} role="presentation" onClick={onClose}>
            <aside
                className={styles.evidenceSidebar}
                role="dialog"
                aria-modal="true"
                aria-label="Evidence Sources"
                onClick={e => e.stopPropagation()}>
                <header className={styles.evidenceSidebarHeader}>
                    <div className={styles.evidenceSidebarHeaderText}>
                        <h2 className={styles.evidenceSidebarTitle}>Evidence Sources</h2>
                        <p className={styles.evidenceSidebarSubtitle}>
                            {cardCount} source{cardCount === 1 ? '' : 's'} used in this response
                        </p>
                    </div>
                    <button
                        type="button"
                        className={styles.evidenceSidebarClose}
                        aria-label="Close evidence sources"
                        onClick={onClose}>
                        <Dismiss16Regular />
                    </button>
                </header>

                {(confidenceLabel || coverageLabel) && (
                    <div className={styles.evidenceConfidenceBanner}>
                        <span className={styles.trustDot} aria-hidden />
                        {confidenceLabel ? (
                            <span className={styles.evidenceBannerStrong}>{confidenceLabel}</span>
                        ) : null}
                        {coverageLabel ? (
                            <>
                                <span className={styles.evidenceBannerSep}>·</span>
                                <span>{coverageLabel}</span>
                            </>
                        ) : null}
                        <span className={styles.evidenceBannerSep}>·</span>
                        <span>{sourcesLabel}</span>
                    </div>
                )}

                <div className={styles.evidenceSidebarBody}>
                    {evidenceItems.length ? (
                        evidenceItems.map(item => (
                            <EvidenceSourceCard
                                key={item.key}
                                item={item}
                                citations={citations}
                                chunks={chunks}
                                onOpen={row => {
                                    onItemClick?.(row, citations, chunks);
                                    onClose?.();
                                }}
                            />
                        ))
                    ) : (
                        <p className={styles.evidenceSidebarEmpty}>
                            {model.emptyMessage || 'No evidence sources for this response.'}
                        </p>
                    )}
                </div>

                {processing > 0 ? (
                    <footer className={styles.evidenceSidebarFooter}>
                        <p>
                            {processing} file{processing === 1 ? '' : 's'} were still processing when
                            this answer was generated and are not reflected in these sources.
                        </p>
                    </footer>
                ) : null}
            </aside>
        </div>,
        document.body,
    );
};

export default AskAiEvidenceSidebar;
